import {useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {useCallback, useRef, useState} from 'react';
import {getSelectedTags} from '../../api/EcaServices';

interface ITag {
  fieldValueId: number;
  fieldValue: string;
  displayOrder: number;
  selected?: boolean;
}

interface IFieldModal {
  fieldId: number;
  fieldName?: string;
  fieldValues: ITag[];
}

interface State {
  isLoading: boolean;
  selectedFieldsAndTags: IFieldModal[];
  isFailedToLoadContent: boolean;
  selectedTagsScope: {
    documentId: string;
    fieldTemplateId?: number;
  } | null;
}

interface SelectedTagsRequestResult {
  data: IFieldModal[] | null;
  failed: boolean;
}

const getSelectedTagsCacheKey = (
  clientId: string,
  projectId: string | null | undefined,
  documentId: string,
  fieldTemplateId?: number
) =>
  JSON.stringify([clientId, projectId, documentId, fieldTemplateId]);

export const useGetSelectedTags = () => {
  const clientId = useSelector(
    (state: RootState) => state.selectedClient.clientId
  );
  const projectId = useSelector((state: RootState) => state.reports.projectId);

  const [state, setState] = useState<State>({
    isLoading: false,
    selectedFieldsAndTags: [] as IFieldModal[],
    isFailedToLoadContent: false,
    selectedTagsScope: null,
  });

  const selectedTagsCache = useRef(new Map<string, IFieldModal[]>());
  const pendingRequests = useRef(
    new Map<string, Promise<SelectedTagsRequestResult>>()
  );
  const cacheGenerations = useRef(new Map<string, number>());
  const requestId = useRef(0);

  const getAllSelectedTags = useCallback(
    async (documentId: string, stdIndexFieldTemplateId?: number) => {
      const cacheKey = getSelectedTagsCacheKey(
        clientId,
        projectId,
        documentId,
        stdIndexFieldTemplateId,
      );
      const scope = {
        documentId,
        fieldTemplateId: stdIndexFieldTemplateId,
      };
      const currentRequestId = ++requestId.current;
      const cacheGeneration =
        cacheGenerations.current.get(cacheKey) ?? 0;
      const cachedTags = selectedTagsCache.current.get(cacheKey);

      if (cachedTags !== undefined) {
        setState((prevState) => ({
          ...prevState,
          selectedFieldsAndTags: cachedTags,
          selectedTagsScope: scope,
          isLoading: false,
          isFailedToLoadContent: false,
        }));
        return;
      }

      setState((prevState) => ({
        ...prevState,
        selectedFieldsAndTags: [],
        selectedTagsScope: scope,
        isLoading: true,
        isFailedToLoadContent: false,
      }));

      let pendingRequest = pendingRequests.current.get(cacheKey);
      if (!pendingRequest) {
        const params = `?clientId=${clientId}&projectId=${projectId}&fieldTemplateId=${stdIndexFieldTemplateId}`;
        pendingRequest = getSelectedTags(documentId, params)
          .then((response) => {
            const selectedFieldsAndTags = response?.data?.data ?? [];
            if (
              (cacheGenerations.current.get(cacheKey) ?? 0) ===
              cacheGeneration
            ) {
              selectedTagsCache.current.set(cacheKey, selectedFieldsAndTags);
            }
            return {data: selectedFieldsAndTags, failed: false};
          })
          .catch(() => ({data: null, failed: true}));
        pendingRequests.current.set(cacheKey, pendingRequest);
      }

      const result = await pendingRequest;
      if (pendingRequests.current.get(cacheKey) === pendingRequest) {
        pendingRequests.current.delete(cacheKey);
      }

      const isCurrentRequest = currentRequestId === requestId.current;
      const isCurrentGeneration =
        (cacheGenerations.current.get(cacheKey) ?? 0) === cacheGeneration;

      if (!isCurrentGeneration) {
        if (isCurrentRequest) {
          setState((prevState) => ({...prevState, isLoading: false}));
        }
        return;
      }

      if (!isCurrentRequest) {
        return;
      }

      if (result.failed) {
        setState((prevState) => ({
          ...prevState,
          selectedFieldsAndTags: [],
          selectedTagsScope: scope,
          isLoading: false,
          isFailedToLoadContent: true,
        }));
        return null;
      }

      setState((prevState) => ({
        ...prevState,
        selectedFieldsAndTags: result.data ?? [],
        selectedTagsScope: scope,
        isLoading: false,
        isFailedToLoadContent: false,
      }));
    },
    [clientId, projectId]
  );

  const invalidateSelectedTags = useCallback(
    (documentId: string, fieldTemplateId?: number) => {
      const cacheKey = getSelectedTagsCacheKey(
        clientId,
        projectId,
        documentId,
        fieldTemplateId
      );
      const currentGeneration = cacheGenerations.current.get(cacheKey) ?? 0;

      cacheGenerations.current.set(cacheKey, currentGeneration + 1);
      selectedTagsCache.current.delete(cacheKey);
      pendingRequests.current.delete(cacheKey);
    },
    [clientId, projectId]
  );

  const resetErrorFlag = useCallback(() => {
    setState((prevState) => ({...prevState, isFailedToLoadContent: false}));
  }, []);

  return {
    getAllSelectedTags,
    isLoading: state.isLoading,
    selectedFieldsAndTags: state.selectedFieldsAndTags,
    isFailedToLoadContent: state.isFailedToLoadContent,
    selectedTagsScope: state.selectedTagsScope,
    invalidateSelectedTags,
    resetErrorFlag,
  };
};
